import React from 'react';
import { Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import type { ProductionQuestion } from '../../../interfaces/ProductionQuestion.interface';
import {
	formatQuestionDateTime,
	formatUserName,
} from './formatters';

const { Text } = Typography;

const ID_COLUMN_WIDTH = 96;

export const ID_COLUMN: ColumnsType<ProductionQuestion>[number] = {
	title: 'ID',
	key: 'uid',
	width: ID_COLUMN_WIDTH,
	render: (_, row) => (
		<Text copyable={{ text: row.uid }} className="whitespace-nowrap">
			{row.uid}
		</Text>
	),
};

interface UserTimestampCellProps {
	user: { full_name?: string; email?: string } | null | undefined;
	timestamp: string | null | undefined;
}

export const UserTimestampCell: React.FC<UserTimestampCellProps> = ({
	user,
	timestamp,
}) => {
	const name = formatUserName(user);
	const when = formatQuestionDateTime(timestamp);

	if (name === '—' && !when) return <>—</>;

	return (
		<div className="flex flex-col leading-snug">
			<span>{name}</span>
			{when && (
				<Text type="secondary" className="text-xs">
					{when}
				</Text>
			)}
		</div>
	);
};

export const REPORTER_COLUMN: ColumnsType<ProductionQuestion>[number] = {
	title: 'Reported By',
	key: 'reported_by',
	render: (_, row) => (
		<UserTimestampCell user={row.reported_by} timestamp={row.created_time} />
	),
};

export const RESOLVED_COLUMN: ColumnsType<ProductionQuestion>[number] = {
	title: 'Resolved By',
	key: 'resolved_at',
	render: (_, row) => (
		<UserTimestampCell user={row.resolved_by} timestamp={row.resolved_at} />
	),
};

export const getLineItemsFromRow = (row: ProductionQuestion) => {
	if (row.order_line_items?.length > 0) {
		return row.order_line_items;
	}

	if (row.order_line_item_id) {
		return [
			{
				id: row.order_line_item_id,
				order_name: row.order_name,
				line_item_name: row.line_item_name,
				thumbnail_file: row.thumbnail_file ?? null,
			},
		];
	}

	return [];
};

const LineItemThumbnail: React.FC<{
	src?: string | null;
	alt?: string;
	size?: number;
}> = ({ src, alt = 'Part preview', size = 48 }) => {
	if (!src) {
		return (
			<div
				className="flex shrink-0 items-center justify-center rounded border border-solid border-gray-200 bg-gray-50 text-[10px] text-gray-400"
				style={{ width: size, height: size }}
			>
				No image
			</div>
		);
	}

	return (
		<img
			src={src}
			alt={alt}
			className="shrink-0 rounded border border-solid border-gray-200 bg-white object-contain"
			style={{ width: size, height: size }}
		/>
	);
};

export const OrderLineItemsCell: React.FC<{ row: ProductionQuestion }> = ({
	row,
}) => {
	const items = getLineItemsFromRow(row);
	const orderName = row.order_name || items[0]?.order_name || '—';
	const thumbnail =
		items[0]?.thumbnail_file ?? row.thumbnail_file ?? null;

	if (orderName === '—' && items.length === 0) return <>—</>;

	return (
		<div className="flex items-center gap-3">
			<LineItemThumbnail
				src={thumbnail}
				alt={items[0]?.line_item_name ?? orderName}
			/>
			<div className="flex min-w-0 flex-col justify-center leading-snug">
				<span>{orderName}</span>
				{items.length > 0 && (
					<div className="flex flex-col">
						{items.map((item) => (
							<Link
								key={item.id}
								to={`/orders/line-items/${item.id}?tab=production-questions`}
								className="text-xs"
							>
								<Text type="secondary" className="text-xs">
									{item.line_item_name}
								</Text>
							</Link>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export const ORDER_LINE_ITEMS_COLUMN: ColumnsType<ProductionQuestion>[number] =
	{
		title: 'Part',
		key: 'order_line_items',
		width: 280,
		render: (_, row) => <OrderLineItemsCell row={row} />,
	};

export const RESOLUTION_CATEGORY_COLUMN: ColumnsType<ProductionQuestion>[number] =
	{
		title: 'Resolution Category',
		key: 'resolution_category_display',
		render: (_, row) => row.resolution_category_display || '—',
	};

export const RESOLUTION_NOTES_COLUMN: ColumnsType<ProductionQuestion>[number] = {
	title: 'Resolution Notes',
	key: 'resolution_description',
	ellipsis: true,
	render: (_, row) => {
		if (!row.resolution_description) {
			return '—';
		}

		return (
			<Tooltip
				title={
					<span className="whitespace-pre-wrap">
						{row.resolution_description}
					</span>
				}
			>
				<Text ellipsis className="block max-w-xs whitespace-pre-wrap">
					{row.resolution_description}
				</Text>
			</Tooltip>
		);
	},
};
